import { ChallengeService } from '@duck/api-definitions'
import { useBootChallengeWithWeapon, WeaponPickCancelledError } from '@/hooks/useBootChallengeWithWeapon'
import { useNavigate } from '@tanstack/react-router'
import { useTar } from '@/modules'
import { toast } from 'sonner'
import { queryClient } from '@/modules/queryClient'
import { availableChallengesQueryKeys } from '@/hooks/useAvailableChallenges'
import { useAuth } from '@/hooks/useAuth'

/**
 * Returns a status-aware `startChallenge` function that:
 *  - Available: boots the challenge (weapon pick if multi-weapon), sets the
 *    `challenge_unbooted` sessionStorage flag, invalidates the available-challenges
 *    cache, then navigates to the workspace route.
 *  - Unbooted: sets the sessionStorage flag (workspace was already booted) and navigates.
 *  - Any other status: navigates directly to the workspace route.
 *
 * The call is wrapped in `loggedInAction` so unauthenticated users are prompted to log in.
 * `WeaponPickCancelledError` is swallowed silently; all other errors show a toast.
 */
export const useStartChallenge = () => {
  const bootChallenge = useBootChallengeWithWeapon()
  const routerNavigate = useNavigate()
  const { t } = useTar()
  const { loggedInAction } = useAuth()

  const startChallenge = (
    challenge: ChallengeService.AvailableChallenge,
    opts?: { preferredWeapon?: ChallengeService.TaskWeaponsOfChoiceEnum },
  ) => {
    loggedInAction('challenge-start', async () => {
      try {
        if (challenge.status === ChallengeService.AvailableChallengeStatusEnum.Available) {
          // When the caller already knows the weapon (e.g. Help-me-pick chose a
          // specific tool), hand boot a single-weapon list so the picker is skipped.
          const preferred = opts?.preferredWeapon
          const allowedWeapons =
            preferred !== undefined && challenge.allowedWeapons.includes(preferred)
              ? [preferred]
              : challenge.allowedWeapons
          const resp = await bootChallenge({
            taskId: challenge.taskId,
            allowedWeapons,
          })
          const slug = resp.data.slug
          if (slug) {
            sessionStorage.setItem('challenge_unbooted', slug)
            void queryClient.invalidateQueries({ queryKey: availableChallengesQueryKeys.all() })
            routerNavigate({ to: `/challenge/${slug}` })
          }
          return
        }
        if (challenge.slug && challenge.status === ChallengeService.AvailableChallengeStatusEnum.Unbooted) {
          sessionStorage.setItem('challenge_unbooted', challenge.slug)
          routerNavigate({ to: `/challenge/${challenge.slug}` })
          return
        }
        if (challenge.slug) {
          routerNavigate({ to: `/challenge/${challenge.slug}` })
        }
      } catch (err) {
        if (err instanceof WeaponPickCancelledError) return
        toast.error(t('challenges.failedToStartChallenge'), { duration: 10000 })
      }
    })
  }

  return startChallenge
}
